/**
 * Runtime shell detection.
 *
 * The same React bundle runs in three places:
 *   1. A normal browser tab (web)
 *   2. An installed PWA on iOS/Android home screen
 *   3. A packaged native shell — Capacitor (mobile) or Electron (desktop)
 *
 * `isNativeShell` is TRUE only when we're inside a packaged app.
 * The installed-PWA case stays FALSE so PWA users still see the
 * marketing landing if they navigate to `/`.
 */

declare global {
  interface Window {
    // Augmented further in electron-bridge.ts with showNativeNotification etc.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    aicavite?: { isElectron?: boolean; platform?: string; [key: string]: any };
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    Capacitor?: { isNativePlatform?: () => boolean; getPlatform?: () => string; [key: string]: any };
  }
}

export function isNativeShell(): boolean {
  if (typeof window === "undefined") return false;
  // Electron preload exposes window.aicavite.isElectron
  if (window.aicavite?.isElectron === true) return true;
  // Capacitor exposes Capacitor.isNativePlatform() on iOS/Android
  if (typeof window.Capacitor?.isNativePlatform === "function") {
    try {
      return window.Capacitor.isNativePlatform();
    } catch {
      /* fall through */
    }
  }
  return false;
}

/**
 * Routes that exist in the SPA but should be HIDDEN inside packaged apps.
 * These are marketing / public-website surfaces — packaged apps are a
 * product, not a brochure.
 */
const MARKETING_PATH_PREFIXES = [
  "/about",
  "/blog",
  "/pricing",
  "/contact",
  "/partner",
  "/privacy-policy",
  "/terms-of-service",
  "/cookie-policy",
];

export function isMarketingPath(pathname: string): boolean {
  if (pathname === "/" || pathname === "/index.html") return true;
  return MARKETING_PATH_PREFIXES.some(
    (p) => pathname === p || pathname.startsWith(`${p}/`)
  );
}
